import { useEffect, useMemo, useRef } from 'react';
import { useAnalytics } from '#~/analytics/hooks/use-analytics.js';
import {
  ALERT_ID,
  ALERT_ACTION,
  ALERT_ACTION_RESULT,
  ALERT_CLOSE_REASON,
  createAlertTracker,
  type AlertShowing
} from '#~/analytics/domains/alert-lifecycle.js';
import { useFeatureFlag } from '#~/posthog/hooks/use-feature-flag.js';
import { authStore, useAuthStore, type RegistryList } from './store.js';

/** The mounted list owns its display identity; the loader owns recovery state. */
export function useRegistryListAlert(list: RegistryList, displayed = true) {
  const state = useAuthStore((store) => store.registryLists[list]);
  const { enabled } = useFeatureFlag('alert_n3_enabled', { defaultValue: true });
  const { track } = useAnalytics();
  const tracker = useMemo(() => createAlertTracker(track), [track]);
  const showing = useRef<AlertShowing | undefined>(undefined);
  const acting = useRef(false);
  const visible = displayed && state?.failed === true && state.eligible && enabled === true;

  useEffect(() => {
    if (visible && !showing.current) {
      showing.current = {
        alert_id: ALERT_ID.N3,
        alert_variant: list,
        showing_id: crypto.randomUUID()
      };
      tracker.trackAlertShown(showing.current);
    } else if (state?.failed === false && showing.current) {
      tracker.trackAlertClosed(showing.current, ALERT_CLOSE_REASON.RESOLVED);
      showing.current = undefined;
    } else if (!visible) {
      // Switching off is not a successful load and must not claim resolution.
      showing.current = undefined;
    }
  }, [list, state?.failed, tracker, visible]);

  return {
    visible,
    retrying: state?.retrying === true,
    retry: async (): Promise<void> => {
      if (!state || state.retrying || acting.current || !showing.current) return;
      acting.current = true;
      const actedShowing = showing.current;
      try {
        const success = await state.retry();
        // A retired account/run must not attribute an old retry to the new person.
        if (authStore.getState().registryLists[list]?.retry !== state.retry) return;
        tracker.trackAlertActed(actedShowing, {
          action: ALERT_ACTION.TRY_AGAIN,
          result: success ? ALERT_ACTION_RESULT.SUCCESS : ALERT_ACTION_RESULT.FAILURE
        });
        // A menu may close while its retry is in flight. Still close that
        // showing on recovery; the mounted effect and this path share the ref.
        if (success && showing.current === actedShowing) {
          tracker.trackAlertClosed(actedShowing, ALERT_CLOSE_REASON.RESOLVED);
          showing.current = undefined;
        }
      } finally {
        acting.current = false;
      }
    }
  };
}
